import { Link2, MonitorSmartphone, UserX, type LucideIcon } from 'lucide-react';
import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useDevices, usePii } from '../../api/queries.ts';
import { PageTitle } from '../../components/ui/feedback.tsx';
import { cn } from '../../lib/cn.ts';
import { DeleteAccountSection } from './DeleteAccountSection.tsx';
import { DevicesSection } from './DevicesSection.tsx';
import { SharedEmailsSection } from './SharedEmailsSection.tsx';
import { tabAfterKey, tabForHash, type TabId } from './tabs.ts';

/**
 * Privacy & security: the questions people come to an account page with, in
 * the order they ask them. Where am I signed in? What have I made public? How
 * do I leave? The way the large account pages group them (devices under
 * security, shared items and deletion under data), as three tabs, one shown
 * at a time.
 *
 * The open tab is the address's #, so a link opens it and Back returns to the
 * previous one. The old addresses land here: /devices → #devices,
 * /shared-emails → #shared, /settings → #delete (router.tsx).
 */

const SECTIONS: Array<{
  id: TabId;
  label: string;
  icon: LucideIcon;
  title: string;
  description: string;
  tone?: 'danger';
}> = [
  {
    id: 'devices',
    label: 'Devices',
    icon: MonitorSmartphone,
    title: 'Where you’re signed in',
    description: 'Phones and computers that use the app with this account, most recently seen first.',
  },
  {
    id: 'shared',
    label: 'Shared emails',
    icon: Link2,
    title: 'Shared emails',
    description: 'Emails you shared as links. Anyone with a link can open it until you delete it.',
  },
  {
    id: 'delete',
    label: 'Delete account',
    icon: UserX,
    title: 'Delete your account',
    description: 'Remove this account and everything stored for it. You cannot undo this.',
    tone: 'danger',
  },
];

const PANELS: Record<TabId, () => ReactNode> = {
  devices: () => <DevicesSection />,
  shared: () => <SharedEmailsSection />,
  delete: () => <DeleteAccountSection />,
};

export function PrivacyPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const devices = useDevices();
  const pii = usePii();
  const tabs = useRef<Array<HTMLButtonElement | null>>([]);

  const activeId = tabForHash(location.hash);
  const active = SECTIONS.find((section) => section.id === activeId)!;
  const open = (id: TabId) => navigate({ hash: id });

  const counts: Partial<Record<TabId, number>> = {
    devices: devices.data ? (devices.data.devices ?? []).length : undefined,
    shared: pii.data ? (pii.data.uploadsInfo ?? []).length : undefined,
  };

  // The tab pattern's keys: arrows move between tabs, Home and End to the ends.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = tabAfterKey(SECTIONS.indexOf(active), event.key, SECTIONS.length);
    if (next === null) return;
    event.preventDefault();
    open(SECTIONS[next]!.id);
    tabs.current[next]?.focus();
  };

  const Icon = active.icon;
  return (
    <>
      <PageTitle description="Where your account is signed in, what you have shared, and how to delete your account.">Privacy & security</PageTitle>

      <div
        role="tablist"
        aria-label="Privacy & security"
        onKeyDown={onKeyDown}
        className="-mx-5 mb-10 flex gap-6 overflow-x-auto border-b border-line px-5 sm:-mx-8 sm:px-8 md:-mx-12 md:px-12"
      >
        {SECTIONS.map(({ id, label }, index) => {
          const selected = id === active.id;
          return (
            <button
              key={id}
              ref={(element) => {
                tabs.current[index] = element;
              }}
              type="button"
              role="tab"
              id={`tab-${id}`}
              aria-selected={selected}
              aria-controls={`panel-${id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => open(id)}
              className={cn(
                '-mb-px flex h-12 shrink-0 items-center gap-2 border-b-2 text-sm font-semibold transition-colors outline-none focus-visible:text-ink',
                selected ? 'border-brand text-ink' : 'border-transparent text-muted hover:text-ink',
                id === 'delete' && !selected && 'hover:text-danger',
              )}
            >
              {label}
              {counts[id] !== undefined ? (
                <span className="rounded-full bg-paper px-2 py-0.5 text-xs tabular-nums text-muted">{counts[id]}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <section key={active.id} role="tabpanel" id={`panel-${active.id}`} aria-labelledby={`tab-${active.id}`} className="page-in">
        <header className="mb-5 flex items-start gap-4">
          <span
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl',
              active.tone === 'danger' ? 'bg-danger-soft text-danger' : 'bg-brand-soft text-brand',
            )}
          >
            <Icon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-xl font-semibold text-ink">{active.title}</h2>
            <p className="mt-0.5 max-w-[60ch] text-[0.95rem] text-muted">{active.description}</p>
          </div>
        </header>
        {PANELS[active.id]()}
      </section>
    </>
  );
}
